/**
 * Users API — all backend calls for the Users tab.
 *
 * Users use a special endpoint (not mytransaction):
 *   Cloud:  VITE_USER_V2_ENDPOINT (/user)       → apiUserManageCloud
 *   Legacy: VITE_USER_ENDPOINT    (/user_manager) → apiUserManageLegacy
 *
 * Each write function checks is_cloud from campus config to pick the correct
 * endpoint and payload shape.
 */

import { apiFetch, apiTransaction, apiUserManageCloud, apiUserManageLegacy } from '../../../services/api.js';
import { getCampusConfig, getSession } from '../../../services/session.js';
import { getAccessSettings, campusToGuid } from '../../../libs.jsx';
import { CURRENT_PORTAL_VERSION } from '../../../constants/portalVersion.js';

// Re-export fetchSchedules from scheduledApi for use on the Users page
export { fetchSchedules } from '../../reports/scheduled/api/scheduledApi.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Returns true if the current campus is cloud-enabled */
function isCloud() {
  return !!getCampusConfig()?.is_cloud;
}

/** Returns the campus GUID for legacy payloads */
function getGuid(campusId) {
  const session = getSession();
  return campusToGuid(session, campusId);
}

// ---------------------------------------------------------------------------
// READ operations
// ---------------------------------------------------------------------------

/**
 * Fetch all users for a campus (with their group assignments).
 *
 * V1 procedure: sp_dev_getgroupsforusersbycampusid
 */
export function fetchUsers(campusId, readOnly = true) {
  return apiFetch({
    procedure: 'sp_dev_getgroupsforusersbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    payload: {is_cloud: isCloud(), campus_guid: getGuid(campusId)},
    readOnly,
  });
}

/**
 * Fetch all user roles (for dropdown and weight lookups).
 *
 * V1: fetchall on att_user_roles
 */
export function fetchRoles() {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_user_roles',
    readOnly: true,
  });
}

/**
 * Fetch all user groups for a campus (for group picker in user add/edit form).
 *
 * V1 procedure: sp_dev_getgroupsbycampusid
 */
export function fetchUserGroupList(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getgroupsbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE operations
// ---------------------------------------------------------------------------

/**
 * One cloud add item. Shared by addUser and addUsers so a single add and a bulk
 * add can never send differently-shaped rows.
 */
function cloudAddItem(data, session, actionStr, taskApproval) {
  return {
    action: actionStr,
    display_name: data.display_name,
    email: (data.email || '').toLowerCase(),
    user_role_id: parseInt(data.user_role_id, 10),
    group_ids: data.group_ids || [],
    username: data.username,
    password: data.password,
    campus_id: session.campusId,
    modified_by: session.userId,
    phone: (data.phone || '').replaceAll('-', ''),
    title: data.title || '',
    reports: data.reports || [],
    is_grandstream: !!data.is_grandstream,
    portal_version: CURRENT_PORTAL_VERSION,
    old_payload: {}, cloud_id: taskApproval ? data.cloud_id : 0
  };
}

/** One legacy add item. Same reason as cloudAddItem. */
function legacyAddItem(data, session, actionStr, taskApproval) {
  return {
    Action: actionStr,
    DisplayName: data.display_name,
    UserEmail: (data.email || '').toLowerCase(),
    UserRole: parseInt(data.user_role_id, 10),
    Migrated: true,
    CampusGuid: getGuid(session.campusId),
    PortalAccess: getAccessSettings(parseInt(data.user_role_id, 10)),
    PhoneNumber: (data.phone || '').replaceAll('-', ''),
    title: data.title || '',
    GroupIds: data.group_ids || [],
    Username: data.username,
    UserPass: data.password,
    CampusId: session.campusId,
    ClientId: session.clientId,
    reports: data.reports || [],
    is_grandstream: !!data.is_grandstream,
    portal_version: CURRENT_PORTAL_VERSION,
    cloud_id: taskApproval ? data.cloud_id : 0
  };
}

/**
 * Add a new user.
 *
 * Cloud:  user-manage-2  action: "new"  (or "new-task" when approving an EHR-staged user)
 * Legacy: user-manage     Action: "new"  (or "new-task" when approving an EHR-staged user)
 *
 * @param {object} data — form data
 * @param {object} session — { campusId, userId, clientId }
 * @param {boolean} taskApproval — when true, uses "new-task" action so the
 *        backend also clears the matching ext_stg_user staged row.
 * @returns {Promise<[boolean, string?]>}
 */
export function addUser(data, session, taskApproval = false) {
  const actionStr = taskApproval ? 'new-task' : 'new';
  if (isCloud()) {
    return apiUserManageCloud({
      payload: [cloudAddItem(data, session, actionStr, taskApproval)],
      clientId: session.clientId,
      userId: session.userId,
    });
  }
  return apiUserManageLegacy({
    payload: [legacyAddItem(data, session, actionStr, taskApproval)],
    userId: session.userId,
  });
}

/**
 * Add several users in ONE request.
 *
 * Not a loop over addUser: the user-manage endpoints already take a list —
 * `deleteUsers` posts N items in a single Payload array today — so a multi-row add
 * is the shape the API expects rather than something worked around. The row
 * objects are built by the same two helpers addUser uses.
 *
 * Backs the CSV upload. Chunking belongs to the caller (see `uploadInChunks` /
 * `UPLOAD_CHUNK_SIZE` in userCsvUpload.js), which calls this once per chunk —
 * nothing here changes with the chunk size.
 *
 * @param {Array<object>} dataList — one form-shaped object per user
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>} whole-request result, as the single add
 */
export function addUsers(dataList, session) {
  const rows = dataList || [];
  if (!rows.length) return Promise.resolve([true]);

  if (isCloud()) {
    return apiUserManageCloud({
      payload: rows.map((d) => cloudAddItem(d, session, 'new', false)),
      clientId: session.clientId,
      userId: session.userId,
    });
  }
  return apiUserManageLegacy({
    payload: rows.map((d) => legacyAddItem(d, session, 'new', false)),
    userId: session.userId,
  });
}

/**
 * Edit an existing user.
 *
 * Cloud:  user-manage-2  action: "update"
 * Legacy: user-manage     Action: "update"
 *
 * @param {object} data — updated user data
 * @param {object} oldData — original user data (for audit trail)
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function editUser(data, oldData, session) {
  if (isCloud()) {
    const payload = [{
      action: 'update',
      user_id: data.user_id,
      display_name: data.display_name,
      email: (data.email || '').toLowerCase(),
      user_role_id: parseInt(data.user_role_id, 10),
      group_ids: data.group_ids || [],
      username: data.username,
      campus_id: session.campusId,
      cognito_id: data.cognito_id,
      modified_by: session.userId,
      phone: (data.phone || '').replaceAll('-', ''),
      title: data.title || '',
      reports: data.reports || [],
      sip_extension: data.sip_extension,
      wave_password: data.wave_password,
      is_grandstream: !!data.is_grandstream,
      oldPayload: {
        user_id: oldData.user_id,
        display_name: oldData.display_name,
        email: oldData.email,
        user_role_id: oldData.user_role_id,
        title: oldData.title,
        group_ids: oldData.group_ids || [],
        username: oldData.username,
        cognito_id: oldData.cognito_id,
        phone: oldData.phone,
        sip_extension: oldData.sip_extension,
        wave_password: oldData.wave_password,
        is_grandstream: !!oldData.is_grandstream,
      },
    }];
    return apiUserManageCloud({
      payload,
      clientId: session.clientId,
      userId: session.userId,
    });
  }

  // Legacy
  const pAccess = getAccessSettings(parseInt(data.user_role_id, 10));
  const payload = [{
    Action: 'update',
    UserId: data.user_id,
    DisplayName: data.display_name,
    UserEmail: (data.email || '').toLowerCase(),
    UserRole: parseInt(data.user_role_id, 10),
    CampusGuid: getGuid(session.campusId),
    title: data.title || '',
    PortalAccess: pAccess,
    GroupIds: data.group_ids || [],
    Username: data.username,
    CampusId: session.campusId,
    /* True for every admin edit (the default). The self-edit on My Profile passes the
       signed-in user's own flag instead, because Migrated switches User_Manager.py
       onto the MySQL write path, and a non-migrated user must stay off it. */
    Migrated: session.migrated ?? true,
    CognitoId: data.cognito_id,
    ClientId: session.clientId,
    PhoneNumber: (data.phone || '').replaceAll('-', ''),
    reports: data.reports || [],
    sip_extension: data.sip_extension,
    wave_password: data.wave_password,
    is_grandstream: !!data.is_grandstream,
  }];
  // PascalCase, because User_Manager.py reads the legacy OldPayload with
  // oldData[i].get("Username") / ("GroupIds") / ("DisplayName") / ("CampusId")
  // / ("UserRole") / ("UserEmail"), and only phone / title / sip_extension in
  // snake_case. This block used to be entirely snake_case, so those six came
  // back None. Two consequences, both fixed by this:
  //
  //   1. the "before" side of every user-edit audit entry was null;
  //   2. `elif displayName != oldData[i].get("DisplayName")` compared against
  //      None, so updateSip fired on EVERY save of a grandstream user that
  //      already had an extension, pushing a redundant PBX updateUser each time.
  //
  // The CLOUD branch above nests its own oldPayload in snake_case and must stay
  // that way -- Portal_User_Manager.py reads it as oldP.get("username").
  const oldPayload = [{
    UserId: oldData.user_id,
    Username: oldData.username,
    DisplayName: oldData.display_name,
    UserEmail: oldData.email,
    UserRole: oldData.user_role_id,
    CampusId: session.campusId,
    GroupIds: oldData.group_ids || [],
    CognitoId: oldData.cognito_id,
    phone: oldData.phone,
    title: oldData.title,
    sip_extension: oldData.sip_extension,
    wave_password: oldData.wave_password,
    is_grandstream: !!oldData.is_grandstream,
  }];
  return apiUserManageLegacy({
    payload,
    oldPayload,
    userId: session.userId,
  });
}

/**
 * Delete one or more users.
 *
 * Cloud:  user-manage-2  action: "remove" (or "remove-task" when approving an EHR-staged delete)
 * Legacy: user-manage     Action: "remove" (or "remove-task" when approving an EHR-staged delete)
 *
 * @param {Array<object>} users — array of { user_id, username, cognito_id, user_role_id }
 * @param {object} session — { campusId, userId, clientId, username }
 * @param {boolean} taskApproval — when true, uses "remove-task" action so the
 *        backend also clears the matching ext_stg_user staged row.
 * @returns {Promise<[boolean, string?]>}
 */
export function deleteUsers(users, session, taskApproval = false) {
  const actionStr = taskApproval ? 'remove-task' : 'remove';
  // sip_extension rides along on each user record; is_grandstream is a
  // campus-level flag pulled from the current campus config (same place
  // addUser/editUser source it from). The backend uses both to clean up
  // the user's SIP registration when applicable.
  const isGrandstream = !!getCampusConfig()?.is_grandstream;
  if (isCloud()) {
    const payload = users.map((u) => ({
      action: actionStr,
      user_id: u.user_id,
      username: u.username,
      campus_id: session.campusId,
      modified_by: session.userId,
      cognito_id: u.cognito_id,
      sip_extension: u.sip_extension,
      is_grandstream: isGrandstream,
      cloud_id: taskApproval ? u.cloud_id : 0
    }));
    return apiUserManageCloud({
      payload,
      clientId: session.clientId,
      userId: session.userId,
    });
  }

  // Legacy
  const payload = users.map((u) => ({
    Action: actionStr,
    UserId: u.user_id,
    Username: u.username,
    CampusId: session.campusId,
    RequestedBy: session.username,
    Migrated: true,
    CognitoId: u.cognito_id,
    ClientId: session.clientId,
    sip_extension: u.sip_extension,
    is_grandstream: isGrandstream,
    cloud_id: taskApproval ? u.cloud_id : 0
  }));
  return apiUserManageLegacy({
    payload,
    userId: session.userId,
  });
}

/**
 * Reset a user's password.
 *
 * Cloud:  user-manage-2  action: "reset"
 * Legacy: user-manage     Action: "reset"
 *
 * @param {object} data — { user_id, username, password }
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function resetPassword(data, session) {
  if (isCloud()) {
    const payload = [{
      action: 'reset',
      campus_id: session.campusId,
      username: data.username,
      password: data.password,
      user_id: data.user_id,
      modified_by: session.userId,
    }];
    return apiUserManageCloud({
      payload,
      clientId: session.clientId,
      userId: session.userId,
    });
  }

  // Legacy
  const payload = [{
    Action: 'reset',
    CampusId: session.campusId,
    Username: data.username,
    UserPass: data.password,
    Migrated: true,
    UserId: data.user_id,
  }];
  return apiUserManageLegacy({
    payload,
    userId: session.userId,
  });
}

// ---------------------------------------------------------------------------
// SELF-SERVICE — the signed-in user's own row (docs/MYREST_MIGRATION_AUDIT.md, section A)
//
// Both used to be /myrest writes that took the user id from the request, so any user could
// be named. The user Lambdas now take the user from the signed-in token and ignore any id
// in the request. Cloud and non-cloud campuses go to their own Lambda, like every other user
// change. Only migrated users have a MySQL row, so the legacy payload carries their flag.
// ---------------------------------------------------------------------------

/**
 * Save the walkthrough version the signed-in user has seen (core_user.portal_version).
 *
 * Cloud:  user-manage-2  action: "portal-version"
 * Legacy: user-manage     Action: "portal-version"
 *
 * @param {string} version — e.g. "4.3.11"
 * @param {object} session — { campusId, userId, clientId, migrated? }
 * @returns {Promise<[boolean, string?]>}
 */
export function savePortalVersion(version, session) {
  if (isCloud()) {
    return apiUserManageCloud({
      payload: [{ action: 'portal-version', campus_id: session.campusId, portal_version: version }],
      clientId: session.clientId,
      userId: session.userId,
    });
  }
  return apiUserManageLegacy({
    payload: [{ Action: 'portal-version', CampusId: session.campusId, PortalVersion: version,
      Migrated: session.migrated ?? true, ClientId: session.clientId }],
    userId: session.userId,
  });
}

/**
 * Record that the signed-in user logged out (a core_user_logs row, type 2).
 *
 * Cloud:  user-manage-2  action: "logout"
 * Legacy: user-manage     Action: "logout"
 *
 * @param {object} session — { campusId, userId, clientId, migrated? }
 * @returns {Promise<[boolean, string?]>}
 */
export function recordLogout(session) {
  if (isCloud()) {
    return apiUserManageCloud({
      payload: [{ action: 'logout', campus_id: session.campusId }],
      clientId: session.clientId,
      userId: session.userId,
    });
  }
  return apiUserManageLegacy({
    payload: [{ Action: 'logout', CampusId: session.campusId,
      Migrated: session.migrated ?? true, ClientId: session.clientId }],
    userId: session.userId,
  });
}

/**
 * Register a SIP extension for one or more users on a grandstream campus.
 * Backend computes/assigns the extension server-side; the frontend only
 * supplies the user ids.
 *
 * @param {Array<number>} userIds — user_ids to register
 * @param {object} session — { campusId, userId, clientId }
 * @returns {Promise<[boolean, string?]>}
 */
export function registerUserSip(userIds, session) {
  const payload = (userIds || []).map((id) => ({ user_id: id }));
  return apiTransaction({
    transType: 'register_user_sip',
    payload,
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}
